import { useMDXComponent } from 'next-contentlayer/hooks';
import MdxImage from './MdxImage';
import Link from 'next/link';
import getSlug from 'speakingurl';

const CustomLink = props => {
  const href = props.href;

  if (href.startsWith('#')) {
    return <a {...props} />;
  }

  // A bare slug means another help article. Nearly thirty of them are written
  // that way — `[the site builder](how-to-use-the-site-builder)` — and without
  // this they fell through to the external branch below: right page, but a new
  // tab, a nofollow, and a full page load instead of a client-side one.
  const internal = href.startsWith('/')
    ? href
    : /^[a-z0-9-]+$/.test(href)
    ? `/help/article/${href}`
    : null;

  if (internal) {
    return (
      <Link {...props} href={internal}>
        {props.children}
      </Link>
    );
  }

  return <a target="_blank" rel="noopener noreferrer nofollow" {...props} />;
};

const components = {
  h2: props => (
    <h2 className="tw-text-2xl" id={getSlug(props.children)} {...props} />
  ),
  h3: props => (
    <h3 className="tw-text-xl" id={getSlug(props.children)} {...props} />
  ),
  h4: props => (
    <h4 className="tw-text-lg" id={getSlug(props.children)} {...props} />
  ),
  a: props => (
    <CustomLink
      className="tw-font-medium tw-text-primary-500 tw-underline hover:tw-underline tw-underline-offset-4 hover:tw-text-primary-800"
      {...props}
    />
  ),
  Prerequisites: props => (
    <div className="tw-mb-4 tw-rounded-md tw-border tw-border-gray-200 tw-bg-white dark:tw-bg-gray-800 tw-px-6 tw-py-1 tw-text-[0.95rem] tw-leading-[1.4rem] tw-shadow-md">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-text-gray-600 dark:tw-text-gray-200">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="tw-w-6 tw-h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z"
          />
        </svg>
        <p className="tw-text-sm tw-font-medium tw-uppercase tw-mt-5">
          Prerequisites
        </p>
      </div>
      {props.children}
    </div>
  ),
  Tip: props => (
    <div className="tw-mb-4 tw-rounded-md tw-border tw-border-gray-200 tw-bg-primary-100 dark:tw-bg-primary-900 tw-px-6 tw-py-1 tw-text-[0.95rem] tw-leading-[1.4rem] tw-shadow-md">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-text-gray-600 dark:tw-text-gray-200">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="tw-w-6 tw-h-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
          />
        </svg>

        <p className="tw-text-sm tw-font-medium tw-uppercase tw-mt-5">Tip</p>
      </div>
      {props.children}
    </div>
  ),
};

export default function MDX({ code, images }) {
  const Component = useMDXComponent(code);

  const MDXImage = props => {
    if (!images) return null;
    const blurDataURL = images.find(image => image.src === props.src)
      ?.blurDataURL;

    return <MdxImage blurDataURL={blurDataURL} {...props} />;
  };

  return (
    <article
      data-mdx-container
      className="postanchors tw-prose tw-prose-gray tw-max-w-none tw-transition-all prose-headings:tw-relative prose-headings:tw-scroll-mt-20 prose-headings:tw-font-display prose-headings:tw-font-bold"
    >
      <Component
        components={{
          ...components,
          Image: MDXImage,
        }}
      />
    </article>
  );
}
